import type {
    ClientLaravelErrorKey,
    ClientLaravelErrorTuple,
    ClientValidationErrorKey,
    ServerErrors,
} from '@/company-portal/add-file/add-file-form-errors.types';
import { CLIENT_VALIDATION_ERROR_KEYS } from '@/company-portal/add-file/add-file-form-errors.types';
import type { ClientValidationErrorKeys, TypedClientError } from '@/components/Views/Company/File/Client/client-info-form.types';
import type { ClientInfoFormRowIndex } from './add-file.types';

/**
 * Extracts the field name from a dotted server error key.
 *
 * 'email.0' => 'email'
 * 'role.1'  => 'role'
 */
export const getErrorField = (key: ClientLaravelErrorKey): ClientValidationErrorKeys => {
    return key.split('.')[0] as ClientValidationErrorKeys;
};

/**
 * Extracts the form row index from a dotted server error key.
 *
 * 'email.0' => '0'
 * 'phone.2' => '2'
 */
export const getFormRowIndex = (key: ClientLaravelErrorKey): ClientInfoFormRowIndex => {
    return `${key.split('.')[1]}`;
};

/**
 * Filters server errors down to entries whose key contains the given field name.
 *
 * filterErrorsByKey({ 'email.0': ['Add a valid email address'], 'role.0': ['Select a role'] }, 'email')
 * => [['email.0', ['Add a valid email address']]]
 */
export const filterErrorsByKey = (errors: ServerErrors, key: ClientValidationErrorKey): ClientLaravelErrorTuple[] => {
    return Object.entries(errors).filter((error): error is ClientLaravelErrorTuple => error[0].includes(key));
};

/**
 * Writes error messages from server error tuples into clientInfoErrors.
 *
 * setClientInfoErrors([['email.0', ['Add a valid email address']]])
 * => clientInfoErrors.get('0').email === 'Add a valid email address'
 */
export const setClientInfoErrors = (
    errorTuples: ClientLaravelErrorTuple[],
    clientInfoErrors: Map<ClientInfoFormRowIndex, TypedClientError>,
) => {
    errorTuples.forEach(error => {
        const [errorKey, errorValueArray] = error;
        const formRowIndex = getFormRowIndex(errorKey);
        const clientInfoError = clientInfoErrors.get(formRowIndex);
        if (clientInfoError) {
            clientInfoError[getErrorField(errorKey)] = errorValueArray.join(' ');
        }
    });
};

/**
 * Extracts email, phone, and role errors from a server error response
 * and writes them into clientInfoErrors for display in the client form.
 */
export const handleClientInfoErrors = (errors: ServerErrors, clientInfoErrors: Map<ClientInfoFormRowIndex, TypedClientError>) => {
    for (const key of CLIENT_VALIDATION_ERROR_KEYS) {
        setClientInfoErrors(filterErrorsByKey(errors, key), clientInfoErrors);
    }
};
